import { FunctionComponent, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import AsyncSearchSelect from "../../../ui/new_components/common/async_search_select/AsyncSearchSelect";
import ItemSelectField from "../../../ui/new_components/entity_selects/ItemSelectField";
import ProjectSelectField from "../../../ui/new_components/entity_selects/ProjectSelectField";
import { urls } from "../../../utils/urls";
import {
  entityLookupApi,
  raiseRequestApi,
} from "../service/request_desk_repository";
import {
  APPLY_FIELD_OPTIONS,
  btnPrimary,
  btnSecondary,
  BUG_MODULES,
  headingBar,
  RD,
  ReasonForError,
  RequestDeskType,
  ResponsibleParty,
  SR_ENTITY_TYPE,
} from "../models/types";

// "Other" isn't in the backend's per-entity apply whitelist — it stays a
// manual-only escape hatch (raised, but never auto-appliable).
const OTHER_FIELD = "other";
const fieldOptionsFor = (entityType: string) => [
  ...(APPLY_FIELD_OPTIONS[entityType] ?? []),
  { value: OTHER_FIELD, label: "Other (manual resolution only)" },
];

const TYPE_CARDS = [
  {
    key: RequestDeskType.service_request,
    icon: "🛠️",
    title: "Service Request",
    desc: "Ask devs to fix data on a PO / SO / ST (e.g. item code change post-inward).",
  },
  {
    key: RequestDeskType.bug,
    icon: "🐞",
    title: "Bug",
    desc: "Something in the ERP is broken or behaving wrongly.",
  },
  {
    key: RequestDeskType.new_requirement,
    icon: "✨",
    title: "New Requirement",
    desc: "A new feature or enhancement you want built.",
  },
];

// A PO has a vendor, an SO a customer, an ST a warehouse route — one slot, three names.
const PARTY_LABEL: Record<string, string> = {
  PO: "Vendor",
  SO: "Customer",
  ST: "Route",
};

const inputCls =
  "p-2 border rounded w-full text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-400";
const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

const RequestDeskCreatePage: FunctionComponent = () => {
  const { showToast } = useToast();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const [type, setType] = useState<string>(RequestDeskType.service_request);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [files, setFiles] = useState<File[]>([]);

  // bug
  const [bug, setBug] = useState({
    module: "",
    steps: "",
    expected: "",
    actual: "",
    environment: "",
  });
  // requirement
  const [req, setReq] = useState({
    justification: "",
    outcome: "",
    desired_date: "",
    department: "",
  });
  // service request
  const [srEntityType, setSrEntityType] = useState<string>(SR_ENTITY_TYPE.PO);
  const [srRef, setSrRef] = useState("");
  const [lookup, setLookup] = useState<any>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [sr, setSr] = useState({
    field: "item_code",
    current_value: "",
    desired_value: "",
    line_ref: "",
    reason_for_error: ReasonForError[0].value,
    reason_detail: "",
    change_note: "",
  });

  const [desiredItem, setDesiredItem] = useState<any>(null);
  const [desiredProject, setDesiredProject] = useState<any>(null);
  const lineItems: any[] = lookup?.line_items ?? [];
  // These fields all describe a change to one line item and need it picked —
  // status/destination_warehouse are document/header-level, not line-level.
  const LINE_LEVEL_FIELDS = [
    "item_code",
    "price",
    "qty",
    "item_type",
    "project_id",
  ];
  // Of those, only these three have a current_value the lookup's line items can
  // actually supply (item_type carries no `type` on a line today) — so only
  // these auto-fill current_value as a read-only field. project_id auto-fills
  // too, but shows the project's NAME rather than a raw id (see applyItem).
  const AUTO_CURRENT_FIELDS = ["item_code", "price", "qty", "project_id"];
  // "Other" document type has no document to look up at all — the whole
  // field/value block is hidden for it. Distinct from "Other" *field* (a real
  // PO/SO/ST document, but the change isn't one of the auto-appliable fields) —
  // conflating the two would hide the field picker as soon as "Other" field is
  // chosen, with no way back.
  const isOther = srEntityType === SR_ENTITY_TYPE.OTHER;
  const isOtherField = sr.field === OTHER_FIELD;
  const isOtherReason = sr.reason_for_error === "Other";
  const needsItem = LINE_LEVEL_FIELDS.includes(sr.field) && !isOtherField;
  const autoCurrentValue =
    AUTO_CURRENT_FIELDS.includes(sr.field) && !isOtherField;
  // Only item_code has a "search the item master for the new value" widget —
  // line items carry no `type` today, so item_type's current/desired values are
  // plain text/select for v1, not auto-derived from the lookup.
  const needsItemMaster = sr.field === "item_code";
  // project_id gets its own picker (ProjectSelectField, same component the PO
  // line-item cards already use) rather than a free-text/select input — the
  // desired value must be a real, open project's id.
  const needsProjectPicker = sr.field === "project_id";

  const applyItem = (field: string, code: string, items: any[] = lineItems) => {
    const it = items.find((x) => x.item_code === code);
    if (!it) return;
    const line = `${it.item_code}${it.name ? " — " + it.name : ""}${it.qty != null ? " (" + it.qty + " units)" : ""}`;
    let cur = sr.current_value;
    if (field === "item_code") cur = it.item_code ?? "";
    else if (field === "price") cur = it.price != null ? String(it.price) : "";
    else if (field === "qty") cur = it.qty != null ? String(it.qty) : "";
    else if (field === "project_id")
      cur = it.project_name || it.project_id || "";
    // item_type: no source value on the line item — leave current_value as
    // whatever the developer has already typed, just record which line.
    setSr((s) => ({ ...s, current_value: cur, line_ref: line }));
  };

  const doLookup = async () => {
    if (!srRef.trim()) {
      showToast({ type: ToastType.error, text: "Enter a document reference" });
      return;
    }
    setLookupLoading(true);
    const res = await entityLookupApi(srEntityType, srRef.trim());
    setLookupLoading(false);
    if (res.success) {
      setLookup(res.data);
      setSelectedItem("");
      setDesiredItem(null);
      setDesiredProject(null);
      setSr((s) => ({
        ...s,
        current_value: "",
        line_ref: "",
        desired_value: "",
      }));
      const items = res.data?.line_items ?? [];
      if (res.data?.found && items.length === 1) {
        const only = items[0];
        setSelectedItem(only.item_code);
        applyItem(sr.field, only.item_code, items);
      }
      if (!res.data?.found) {
        showToast({
          type: ToastType.error,
          text: res.data?.message || `No ${srEntityType} found for "${srRef}"`,
        });
      }
    } else {
      showToast({ type: ToastType.error, text: res.error || "Lookup failed" });
    }
  };

  const buildPayload = () => {
    const base: any = { type, title, description, priority };
    if (type === RequestDeskType.bug) base.bug = bug;
    else if (type === RequestDeskType.new_requirement) base.requirement = req;
    else if (type === RequestDeskType.service_request) {
      base.service_request = {
        entity_type: srEntityType,
        // No document and no field/value change for "Other" — don't ship values
        // the user was never shown.
        entity_ref: isOther ? "" : srRef.trim(),
        // "Other" field on a real document: still a genuine PO/SO/ST reference
        // above, just no structured field/value to auto-apply — the change_note
        // carries the description instead, and this SR resolves manually only.
        change_requested:
          isOther || isOtherField
            ? { field: "", current_value: "", desired_value: "", line_ref: "" }
            : {
                field: sr.field,
                current_value: sr.current_value,
                desired_value: sr.desired_value,
                line_ref: sr.line_ref,
              },
        change_note: sr.change_note,
        reason_for_error: sr.reason_for_error,
        reason_detail: isOtherReason ? sr.reason_detail.trim() : "",
      };
    }
    return base;
  };

  const validate = (): string | null => {
    if (!title.trim()) return "Title is required";
    if (!description.trim()) return "Description is required";
    if (type === RequestDeskType.bug) {
      if (!bug.module || !bug.steps || !bug.expected || !bug.actual)
        return "Bug needs module, steps, expected and actual";
    }
    if (type === RequestDeskType.new_requirement) {
      if (!req.justification || !req.outcome)
        return "Requirement needs justification and outcome";
    }
    if (type === RequestDeskType.service_request) {
      // "Other" carries no document and no change_requested — only the reason and
      // the free-text description below apply.
      if (!isOther) {
        if (!srRef.trim()) return "Document reference is required";
        if (!lookup?.found)
          return "Look up and confirm the document before submitting";
        if (!sr.field) return "Field to change is required";
        if (needsItem && !selectedItem) return "Select the item line to change";
        if (needsItemMaster && !sr.desired_value)
          return "Search and select the desired item";
        if (needsProjectPicker && !sr.desired_value)
          return "Search and select the destination project";
      }
      if (!sr.reason_for_error) return "Reason for error is required";
      if (isOtherReason && !sr.reason_detail.trim())
        return "Please specify the reason for the error";
      if (!sr.change_note.trim())
        return "Explanation / change note is required";
    }
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      showToast({ type: ToastType.error, text: err });
      return;
    }
    setLoading(true);
    const res = await raiseRequestApi(buildPayload(), files);
    setLoading(false);
    if (res.success) {
      showToast({
        type: ToastType.success,
        text: res.message || `Request ${res.data?.request_no} created`,
      });
      history.push(urls.request_desk_list);
    } else {
      showToast({
        type: ToastType.error,
        text: res.error || "Failed to create",
      });
    }
  };

  const parties: ResponsibleParty[] = lookup?.responsible_parties ?? [];

  return (
    <div className="p-4">
      {loading && <LoadingWidget />}
      <h1
        className="text-lg font-semibold border-l-4 pl-2 mb-4"
        style={headingBar}
      >
        Raise Request
      </h1>

      {/* type selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        {TYPE_CARDS.map((c) => (
          <div
            key={c.key}
            onClick={() => setType(c.key)}
            className="cursor-pointer rounded-lg border-2 p-4 bg-white"
            style={
              type === c.key
                ? { borderColor: RD.accent, backgroundColor: RD.accentSoft }
                : { borderColor: "#e5e7eb" }
            }
          >
            <div className="text-2xl mb-1">{c.icon}</div>
            <div className="font-semibold text-sm">{c.title}</div>
            <div className="text-xs text-gray-500 mt-1">{c.desc}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        {/* common */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className={labelCls}>
              Title <span style={{ color: RD.red }}>*</span>
            </label>
            <input
              className={inputCls}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary"
            />
          </div>
          <div>
            <label className={labelCls}>
              Priority <span style={{ color: RD.red }}>*</span>
            </label>
            <select
              className={inputCls}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>
            Description <span style={{ color: RD.red }}>*</span>
          </label>
          <textarea
            className={inputCls}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* BUG */}
        {type === RequestDeskType.bug && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                Module / screen <span style={{ color: RD.red }}>*</span>
              </label>
              <select
                className={inputCls}
                value={bug.module}
                onChange={(e) => setBug({ ...bug, module: e.target.value })}
              >
                <option value="">Select module</option>
                {BUG_MODULES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Environment</label>
              <input
                className={inputCls}
                value={bug.environment}
                onChange={(e) =>
                  setBug({ ...bug, environment: e.target.value })
                }
                placeholder="browser / URL"
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>
                Steps to reproduce <span style={{ color: RD.red }}>*</span>
              </label>
              <textarea
                className={inputCls}
                rows={2}
                value={bug.steps}
                onChange={(e) => setBug({ ...bug, steps: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>
                Expected <span style={{ color: RD.red }}>*</span>
              </label>
              <textarea
                className={inputCls}
                rows={2}
                value={bug.expected}
                onChange={(e) => setBug({ ...bug, expected: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>
                Actual <span style={{ color: RD.red }}>*</span>
              </label>
              <textarea
                className={inputCls}
                rows={2}
                value={bug.actual}
                onChange={(e) => setBug({ ...bug, actual: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* NEW REQUIREMENT */}
        {type === RequestDeskType.new_requirement && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={labelCls}>
                Business justification <span style={{ color: RD.red }}>*</span>
              </label>
              <textarea
                className={inputCls}
                rows={2}
                value={req.justification}
                onChange={(e) =>
                  setReq({ ...req, justification: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>
                Expected outcome <span style={{ color: RD.red }}>*</span>
              </label>
              <textarea
                className={inputCls}
                rows={2}
                value={req.outcome}
                onChange={(e) => setReq({ ...req, outcome: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Desired date</label>
              <input
                type="date"
                className={inputCls}
                value={req.desired_date}
                onChange={(e) =>
                  setReq({ ...req, desired_date: e.target.value })
                }
              />
            </div>
            <div>
              <label className={labelCls}>Benefiting department</label>
              <input
                className={inputCls}
                value={req.department}
                onChange={(e) => setReq({ ...req, department: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* SERVICE REQUEST */}
        {type === RequestDeskType.service_request && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className={labelCls}>
                  Document type <span style={{ color: RD.red }}>*</span>
                </label>
                <select
                  className={inputCls}
                  value={srEntityType}
                  onChange={(e) => {
                    const nextEntityType = e.target.value;
                    setSrEntityType(nextEntityType);
                    setLookup(null);
                    setSelectedItem("");
                    setDesiredItem(null);
                    setDesiredProject(null);
                    setSr((s) => ({
                      ...s,
                      // the field list is entity-specific (e.g. ST has no
                      // price, only PO has status) — fall back to that
                      // entity's first supported field rather than keep one
                      // that may no longer be valid.
                      field: fieldOptionsFor(nextEntityType)[0]?.value ?? "",
                      current_value: "",
                      line_ref: "",
                      desired_value: "",
                    }));
                  }}
                >
                  <option value="PO">Purchase Order (PO)</option>
                  <option value="SO">Sales Order (SO)</option>
                  <option value="ST">Stock Transfer (ST)</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              {!isOther && (
                <div>
                  <label className={labelCls}>
                    Document reference <span style={{ color: RD.red }}>*</span>
                  </label>
                  <input
                    className={inputCls}
                    value={srRef}
                    onChange={(e) => setSrRef(e.target.value)}
                    placeholder="PO number / Order ID / ST ID"
                  />
                </div>
              )}
              {!isOther && (
                <button
                  className="rounded px-3 py-2 text-sm font-semibold disabled:opacity-60"
                  style={btnPrimary}
                  onClick={doLookup}
                  disabled={lookupLoading}
                >
                  {lookupLoading ? "Looking up…" : "Look up"}
                </button>
              )}
            </div>

            {lookup?.found && (
              <div
                className="rounded-lg p-3"
                style={{
                  border: `1px solid ${RD.accentLine}`,
                  backgroundColor: RD.accentSoft,
                }}
              >
                <div className="flex justify-between text-sm font-semibold">
                  <span>{lookup.title}</span>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    {lookup.entity_stage}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2 text-gray-600">
                  <div>
                    <div className="text-gray-400">
                      {PARTY_LABEL[lookup.entity_type] ?? "Party"}
                    </div>
                    {lookup.customer || lookup.vendor || "—"}
                  </div>
                  <div>
                    <div className="text-gray-400">Value</div>
                    {lookup.value ?? "—"}
                  </div>
                  <div>
                    <div className="text-gray-400">Created by</div>
                    {lookup.created_by_name || "—"}
                  </div>
                  <div>
                    <div className="text-gray-400">Fulfillment</div>
                    {lookup.fulfillment_status}
                  </div>
                </div>
                <div
                  className="mt-2 pt-2"
                  style={{ borderTop: `1px solid ${RD.accentLine}` }}
                >
                  <div className="text-xs uppercase text-gray-500 mb-1">
                    ⚠ Responsible parties
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {parties.length === 0 && (
                      <span className="text-xs text-gray-400">
                        None found on this document
                      </span>
                    )}
                    {parties.map((p, i) => (
                      <span
                        key={i}
                        className="bg-white border rounded-full px-2 py-1 text-xs"
                      >
                        <b>{p.name || p.uid}</b> · {p.role}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Field/value details describe a change to a looked-up document —
                "Other" has none, so the whole block is dropped. */}
            {!isOther && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>
                    Field to change <span style={{ color: RD.red }}>*</span>
                  </label>
                  <select
                    className={inputCls}
                    value={sr.field}
                    onChange={(e) => {
                      const field = e.target.value;
                      setSr((s) => ({
                        ...s,
                        field,
                        current_value: "",
                        desired_value: "",
                      }));
                      if (field === OTHER_FIELD) {
                        setSelectedItem("");
                      } else if (selectedItem) {
                        applyItem(field, selectedItem);
                      }
                      if (field !== "item_code") setDesiredItem(null);
                      if (field !== "project_id") setDesiredProject(null);
                    }}
                  >
                    {fieldOptionsFor(srEntityType).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {needsItem && lookup?.found && (
                  <div>
                    <label className={labelCls}>
                      Item line <span style={{ color: RD.red }}>*</span>{" "}
                      <small>(search the item to change)</small>
                    </label>
                    <AsyncSearchSelect<any>
                      value={
                        lineItems.find((x) => x.item_code === selectedItem) ??
                        null
                      }
                      onSelect={(it) => {
                        setSelectedItem(it.item_code);
                        applyItem(sr.field, it.item_code);
                      }}
                      minChars={0}
                      size="md"
                      placeholder="Search item in this document…"
                      fetcher={(q) =>
                        Promise.resolve(
                          q
                            ? lineItems.filter((it) =>
                                `${it.item_code} ${it.name}`
                                  .toLowerCase()
                                  .includes(q.toLowerCase()),
                              )
                            : lineItems,
                        )
                      }
                      getOptionLabel={(it) =>
                        it
                          ? `${it.item_code}${it.name ? " — " + it.name : ""}`
                          : ""
                      }
                      getOptionKey={(it) => it?.item_code ?? ""}
                      renderOption={(it) => (
                        <div className="text-xs">
                          <b>{it.item_code}</b>
                          {it.name ? ` — ${it.name}` : ""}
                          {it.qty != null ? ` (${it.qty})` : ""}
                          {it.section ? (
                            <span className="text-gray-400">
                              {" "}
                              · {it.section}
                            </span>
                          ) : null}
                        </div>
                      )}
                    />
                    {lineItems.length === 0 && (
                      <div className="text-xs mt-1" style={{ color: RD.red }}>
                        No line items found on this document — pick “Other” and
                        describe the change instead.
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className={labelCls}>
                    Current value {autoCurrentValue && <small>(auto)</small>}
                  </label>
                  <input
                    className={inputCls}
                    value={sr.current_value}
                    readOnly={autoCurrentValue}
                    style={
                      autoCurrentValue
                        ? { backgroundColor: "#f1f3f6" }
                        : undefined
                    }
                    placeholder={
                      autoCurrentValue
                        ? "Select an item line above"
                        : sr.field === "destination_warehouse"
                          ? "Current warehouse id"
                          : ""
                    }
                    onChange={(e) =>
                      setSr({ ...sr, current_value: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Desired value{" "}
                    {needsItemMaster && <small>(search item master)</small>}
                    {needsProjectPicker && <small>(search project)</small>}
                  </label>
                  {needsItemMaster ? (
                    <ItemSelectField
                      value={desiredItem}
                      placeholder="Search new item by name / code"
                      onSelect={(it: any) => {
                        setDesiredItem(it);
                        setSr((s) => ({
                          ...s,
                          desired_value: it?.item_code ?? "",
                        }));
                      }}
                    />
                  ) : needsProjectPicker ? (
                    <ProjectSelectField
                      value={desiredProject}
                      placeholder="Search destination project"
                      onSelect={(p: any) => {
                        setDesiredProject(p);
                        setSr((s) => ({ ...s, desired_value: p?.id ?? "" }));
                      }}
                    />
                  ) : sr.field === "item_type" ? (
                    <select
                      className={inputCls}
                      value={sr.desired_value}
                      onChange={(e) =>
                        setSr({ ...sr, desired_value: e.target.value })
                      }
                    >
                      <option value="">Select type</option>
                      <option value="Product">Product</option>
                      <option value="Service">Service</option>
                    </select>
                  ) : sr.field === "status" ? (
                    <select
                      className={inputCls}
                      value={sr.desired_value}
                      onChange={(e) =>
                        setSr({ ...sr, desired_value: e.target.value })
                      }
                    >
                      <option value="">Select status</option>
                      <option value="Open">Open (reopen)</option>
                    </select>
                  ) : (
                    <input
                      className={inputCls}
                      value={sr.desired_value}
                      placeholder={
                        sr.field === "destination_warehouse"
                          ? "New warehouse id"
                          : ""
                      }
                      onChange={(e) =>
                        setSr({ ...sr, desired_value: e.target.value })
                      }
                    />
                  )}
                </div>
                <div className="md:col-span-3">
                  <label className={labelCls}>Line item reference</label>
                  <input
                    className={inputCls}
                    value={sr.line_ref}
                    onChange={(e) => setSr({ ...sr, line_ref: e.target.value })}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className={labelCls}>
                  Reason for the error <span style={{ color: RD.red }}>*</span>{" "}
                  (feeds fault dashboard)
                </label>
                <select
                  className={inputCls}
                  value={sr.reason_for_error}
                  onChange={(e) =>
                    setSr({
                      ...sr,
                      reason_for_error: e.target.value,
                      reason_detail:
                        e.target.value === "Other" ? sr.reason_detail : "",
                    })
                  }
                >
                  {ReasonForError.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              {isOtherReason && (
                <div className="md:col-span-2">
                  <label className={labelCls}>
                    Specify the reason <span style={{ color: RD.red }}>*</span>
                  </label>
                  <input
                    className={inputCls}
                    value={sr.reason_detail}
                    onChange={(e) =>
                      setSr({ ...sr, reason_detail: e.target.value })
                    }
                    placeholder="Describe the reason"
                  />
                </div>
              )}
              <div className="md:col-span-3">
                <label className={labelCls}>
                  Explanation / change note{" "}
                  <span style={{ color: RD.red }}>*</span>
                </label>
                <textarea
                  className={inputCls}
                  rows={2}
                  value={sr.change_note}
                  onChange={(e) =>
                    setSr({ ...sr, change_note: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        )}

        <div>
          <label className={labelCls}>Attachments</label>
          <input
            type="file"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            className="text-sm"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            className="rounded px-4 py-2 text-sm font-semibold"
            style={btnPrimary}
            onClick={onSubmit}
          >
            Submit Request
          </button>
          <button
            className="rounded px-4 py-2 text-sm"
            style={btnSecondary}
            onClick={() => history.push(urls.request_desk_list)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestDeskCreatePage;
